import { z } from "zod";

export const backgroundStyleEnum = z.enum([
  "primary", "secondary", "surface", "dark", "brand", "light",
  "tertiary", "primaryBg", "secondaryBg", "tertiaryBg",
]);

export const overlayOpacityEnum = z.enum(["none", "light", "medium", "heavy", "light-glass", "light-frost"]);

export const globalSectionSchema = z.object({
  backgroundStyle: backgroundStyleEnum.default("surface"),
  density: z.enum(["compact", "normal", "spacious", "full"]).default("normal"),
  width: z.enum(["contained", "wide", "full"]).default("contained"),
  headingLevel: z.enum(["h1", "h2", "h3"]).default("h2"),
  topDivider: z.boolean().default(false),
  bottomDivider: z.boolean().default(false),
  hideOnMobile: z.boolean().default(false),
  backgroundImageUrl: z.string().optional(),
  overlayOpacity: overlayOpacityEnum.default("none"),
  backgroundPosition: z.string().default("50% 50%"),
});

export type GlobalSectionProps = z.infer<typeof globalSectionSchema>;

// Two density scales (UX Decision #1):
// Production legalhd.net uses ~48px vertical section padding (py-12).
// normal matches production; compact is tighter; spacious is generous.
export const STANDARD_DENSITY = {
  compact:  "py-6 @md:py-8",
  normal:   "py-10 @md:py-12",
  spacious: "py-16 @md:py-24",
  full:     "py-10 @md:py-12",   // fallback: same as "normal" (full height is Hero-only)
} as const;

export const HERO_DENSITY = {
  compact:  "py-16 @md:py-24",
  normal:   "py-20 @md:py-32",
  spacious: "py-24 @md:py-40",
  full:     "py-0",              // no padding; height comes from min-h on the section
} as const;

// Two width scales (UX Decision #2):
export const STANDARD_WIDTH = {
  contained: "max-w-6xl mx-auto px-6",
  wide:      "max-w-7xl mx-auto px-6",
  full:      "mx-auto px-6",
} as const;

export const NARROW_WIDTH = {
  contained: "max-w-3xl mx-auto px-6",
  wide:      "max-w-4xl mx-auto px-6",
  full:      "max-w-5xl mx-auto px-6",
} as const;

// Measure for body/subheading copy *inside* a section container. Separate from
// the two width scales above, which size the container itself. "medium" is the
// house default every non-Hero section hardcodes as `max-w-2xl`.
export const TEXT_MEASURE = {
  narrow: "max-w-xl",
  medium: "max-w-2xl",
  full:   "",
} as const;

// Helper: get sub-heading tag to keep hierarchy valid
export function subHeadingLevel(main: "h1" | "h2" | "h3"): "h2" | "h3" | "h4" {
  const map = { h1: "h2", h2: "h3", h3: "h4" } as const;
  return map[main];
}

/** Backward-compat mapping from named tokens to CSS percentage values. */
const NAMED_POSITION_CSS: Record<string, string> = {
  "top-left": "0% 0%",
  "top": "50% 0%",
  "top-right": "100% 0%",
  "left": "0% 50%",
  "center": "50% 50%",
  "right": "100% 50%",
  "bottom-left": "0% 100%",
  "bottom": "50% 100%",
  "bottom-right": "100% 100%",
};

/**
 * Normalize a background position value to a CSS-ready string.
 * Accepts both old named tokens ("center", "top-left") and new
 * percentage strings ("25% 75%"). Named tokens are mapped to their
 * percentage equivalents; percentage strings pass through as-is.
 */
export function normalizeBgPosition(value: string): string {
  return NAMED_POSITION_CSS[value] || value;
}

// Overlay opacity classes (shared by SectionShell and bare-mode sections)
export const OVERLAY_CLASSES: Record<string, string> = {
  none: "bg-black/0",
  light: "bg-black/20",
  medium: "bg-black/40",
  heavy: "bg-black/60",
  "light-glass": "bg-white/40",
  "light-frost": "bg-white/60",
};
